/**
 * Compact plan progress indicator displayed above the chat input.
 * Shows current task and overall progress at a glance.
 * Clicking expands to show the full plan in the sidebar.
 */

import React from 'react';
import {
  ListChecks,
  CheckCircle2,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import type {
  PlanningStrategy,
  OrchestratedStatusDict,
  ResearchStatusDict,
} from '../../types/planning';

type PlanStatusType = OrchestratedStatusDict | ResearchStatusDict | null;

interface PlanProgressIndicatorProps {
  strategy: PlanningStrategy | null;
  status: PlanStatusType;
  isPlanActive: boolean;
  onOpenPanel?: () => void;
}

export function PlanProgressIndicator({
  strategy,
  status,
  isPlanActive,
  onOpenPanel,
}: PlanProgressIndicatorProps) {
  if (!isPlanActive || !status) {
    return null;
  }

  const isOrchestrated = strategy === 'orchestrated' || strategy === 'notebook';
  const orchestratedStatus = isOrchestrated ? (status as OrchestratedStatusDict) : null;
  const subtasks = orchestratedStatus?.subtasks ?? [];

  const completedCount = subtasks.filter(
    (s) => s.state === 'completed' || s.state === 'done'
  ).length;
  const totalCount = subtasks.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Find current subtask
  const currentSubtask = subtasks.find((s) => s.is_current);
  const currentIndex = currentSubtask ? subtasks.indexOf(currentSubtask) : -1;

  // Determine if all tasks are complete
  const isComplete = completedCount === totalCount && totalCount > 0;

  return (
    <button
      onClick={onOpenPanel}
      className={`
        w-full flex items-center gap-3 px-4 py-2.5
        border-t transition-colors text-left
        ${isComplete
          ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-900 hover:bg-emerald-100 dark:hover:bg-emerald-900/30'
          : 'bg-sky-50 dark:bg-sky-900/20 border-sky-100 dark:border-sky-900 hover:bg-sky-100 dark:hover:bg-sky-900/30'
        }
      `}
      data-testid="plan-progress-indicator"
    >
      {/* Icon */}
      <div className={`
        flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center
        ${isComplete ? 'bg-emerald-100 dark:bg-emerald-900/40' : 'bg-sky-100 dark:bg-sky-900/40'}
      `}>
        {isComplete ? (
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
        ) : (
          <ListChecks className="w-4 h-4 text-sky-600" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {/* Progress counter */}
          <span className={`
            text-xs font-semibold
            ${isComplete ? 'text-emerald-700 dark:text-emerald-400' : 'text-sky-700 dark:text-sky-400'}
          `}>
            {completedCount}/{totalCount} Tasks
          </span>

          {/* Progress bar (mini) */}
          <div className="flex-1 max-w-[100px] h-1.5 bg-white/60 dark:bg-white/10 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                isComplete ? 'bg-emerald-500' : 'bg-sky-500'
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Current task indicator */}
          {currentSubtask && !isComplete && (
            <div className="flex items-center gap-1 text-xs text-sky-600 dark:text-sky-400">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span className="hidden sm:inline">Step {currentIndex + 1}</span>
            </div>
          )}
        </div>

        {/* Current task name */}
        {currentSubtask && !isComplete && (
          <p className="text-xs text-gray-600 dark:text-gray-400 truncate mt-0.5">
            {currentSubtask.name || currentSubtask.description}
          </p>
        )}

        {/* Completion message */}
        {isComplete && (
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
            All tasks completed successfully
          </p>
        )}
      </div>

      {/* Expand indicator */}
      <ChevronRight className={`
        w-4 h-4 flex-shrink-0
        ${isComplete ? 'text-emerald-400' : 'text-sky-400'}
      `} />
    </button>
  );
}

export default PlanProgressIndicator;
